export const knowledgeIntro = {
  eyebrow: 'Knowledge Hub',
  title: 'TNT Coach AI — Top Notch Transformation Knowledge Catalogue',
  description:
    'TNT Coach AI is an AI-powered personal-development and self-coaching companion from Top Notch Technologies. It is designed to help users think more clearly, build confidence, make decisions, strengthen habits and turn challenges into practical next actions.',
};

export const knowledgeTopics = [
  { id: 'tnt-about', label: 'About TNT Coach AI' },
  { id: 'tnt-coaching', label: 'Coaching & Use Cases' },
  { id: 'tnt-features', label: 'Features' },
  { id: 'tnt-goals', label: 'Goals, Habits & Progress' },
  { id: 'tnt-access', label: 'Access & Pricing' },
  { id: 'tnt-safety', label: 'Scope & Safety' },
];

export const knowledgeGroups = [
  {
    id: 'tnt-about',
    title: 'About TNT Coach AI',
    faqs: [
      {
        question: '1. What is TNT Coach AI?',
        answer:
          'TNT Coach AI is an AI-powered personal-development and self-coaching companion designed to provide instant, practical guidance for goals, habits, confidence, decision-making, leadership, motivation and personal growth.',
      },
      {
        question: '2. Who is behind TNT Coach AI?',
        answer:
          'TNT Coach AI is part of the Top Notch Transformation coaching offering presented by Top Notch Technologies at topnotchtechnologies.net.',
      },
      {
        question: '3. What problem does TNT Coach AI solve?',
        answer:
          'The app is designed for moments when a person feels stuck, uncertain, unmotivated, hesitant, overwhelmed by a decision or unsure what practical step to take next. It provides an immediate coaching-style perspective rather than requiring the user to wait for a scheduled coaching session.',
      },
      {
        question: '4. What does "Top Notch Transformation" mean?',
        answer:
          'Top Notch Transformation is the personal-development approach behind TNT Coach AI. Its emphasis is on clearer thinking, confident action and consistent personal growth through questions, reframes, decisions and practical commitments.',
      },
      {
        question: '5. Is TNT Coach AI a chatbot?',
        answer:
          'TNT Coach AI uses artificial intelligence conversationally, but it is designed specifically around self-coaching. Its purpose is to help users examine a challenge, gain a useful perspective and identify an action rather than simply provide general conversation.',
      },
      {
        question: '6. What kind of response does TNT Coach AI provide?',
        answer:
          "A typical coaching response may identify what the app hears in the user's situation, explore the deeper issue, offer a mindset reframe, ask a coaching question, suggest a practical next step and encourage a simple action commitment.",
      },
    ],
  },
  {
    id: 'tnt-coaching',
    title: 'Coaching Styles & Use Cases',
    faqs: [
      {
        question: '7. What coaching styles are available in TNT Coach AI?',
        answer:
          'The platform presents multiple coaching perspectives, including Peak Performance, Action & Confidence, Champion Mindset, Inspirational Leadership, Habit Design, Business Growth, Calm Thinking and Purpose coaching.',
      },
      {
        question: '8. What is the Peak Performance Coach for?',
        answer:
          'The Peak Performance coaching style is intended for questions involving motivation, personal standards, identity, energy, ambition and personal power.',
      },
      {
        question: '9. What is the Action & Confidence Coach for?',
        answer:
          'The Action & Confidence coaching style is intended to help users work through hesitation, procrastination, fear, self-doubt and difficulty taking the next step.',
      },
      {
        question: '10. What is the Champion Mindset Coach for?',
        answer:
          'The Champion Mindset style focuses on discipline, resilience, focus, consistency, competition with personal standards and the repetition needed to build confidence through action.',
      },
      {
        question: '11. What is the Inspirational Leadership Coach for?',
        answer:
          'This coaching perspective helps users think about leadership, values, service, purpose, responsibility, meaning and the way they influence or support other people.',
      },
      {
        question: '12. What is the Habit Design Coach for?',
        answer:
          'The Habit Design Coach is focused on routines, consistency, behaviour change, small repeatable actions and building systems that make desired behaviours easier to maintain.',
      },
      {
        question: '13. What is the Business Growth Coach for?',
        answer:
          'The Business Growth coaching style is intended for business-related thinking such as offers, sales, marketing, strategy, execution and income goals. It is a coaching and planning tool, not professional financial advice.',
      },
      {
        question: '14. What is the Calm Thinking Coach for?',
        answer:
          'The Calm Thinking style is designed for situations involving overthinking, worry, emotional decision-making and the need to slow down, separate facts from assumptions and gain clearer perspective.',
      },
      {
        question: '15. What is the Purpose Coach for?',
        answer:
          'The Purpose coaching style helps users explore direction, identity, fulfilment, contribution, meaning and longer-term personal growth.',
      },
      {
        question: '16. Can TNT Coach AI help with procrastination?',
        answer:
          'Yes. The platform includes an Action & Confidence perspective and a dedicated Procrastination Breaker designed to help users identify what they are avoiding and choose the smallest useful next action.',
      },
      {
        question: '17. Can TNT Coach AI help with confidence?',
        answer:
          'Yes. Users can explore fear, self-doubt and hesitation, reframe the issue and identify an action that helps build confidence through practical experience rather than waiting to feel completely ready.',
      },
      {
        question: '18. Can TNT Coach AI help with difficult decisions?',
        answer:
          'Yes. The Decision Coach is designed to help users structure a difficult choice, consider the situation more calmly and identify what information, values or consequences matter before taking action.',
      },
      {
        question: '19. Can TNT Coach AI help business owners?',
        answer:
          'Yes. The Business Growth coaching perspective can be used to think through offers, marketing, sales, priorities, execution and business goals. Users should still seek qualified professional advice when a decision requires legal, tax, investment, accounting or other regulated expertise.',
      },
      {
        question: '20. Can TNT Coach AI help with leadership challenges?',
        answer:
          'Yes. Users can use the Inspirational Leadership perspective to reflect on values, responsibility, communication, service, influence, standards and the kind of leader they want to become.',
      },
    ],
  },
  {
    id: 'tnt-features',
    title: 'Features & How It Works',
    faqs: [
      {
        question: '21. How does TNT Coach AI work?',
        answer:
          'Users describe a challenge, question, fear or goal in their own words, choose a coaching style and receive an AI-generated coaching response. They can then select a practical action, record an insight and use the platform to support ongoing progress.',
      },
      {
        question: '22. What is Ask Coach?',
        answer:
          'Ask Coach is the core feature that allows a user to enter a question, challenge, fear or goal and receive a coaching-style response.',
      },
      {
        question: '23. What is Compare Coach Views?',
        answer:
          'Compare Coach Views lets a user examine the same challenge from more than one coaching perspective. This can reveal different ways of understanding a problem and help the user decide which perspective is most useful.',
      },
      {
        question: '24. What is the Daily Coaching Check-In?',
        answer:
          'The Daily Coaching Check-In is designed to help users begin the day with greater focus and intention by identifying what matters and choosing a useful action.',
      },
      {
        question: '25. What is Decision Coach?',
        answer:
          'Decision Coach is a structured self-coaching tool for working through choices and reducing unproductive overthinking around decisions.',
      },
      {
        question: '26. What is Goal Coach?',
        answer:
          'Goal Coach helps users move from a broad goal toward clearer plans, habits and next actions.',
      },
      {
        question: '27. What is the Procrastination Breaker?',
        answer:
          'The Procrastination Breaker helps users identify an avoided task or decision, understand what may be creating resistance and choose a smaller, more manageable action.',
      },
      {
        question: '28. Does TNT Coach AI include a coaching journal?',
        answer:
          'Yes. The Coaching Journal is designed to let users save coaching sessions or insights so they can revisit patterns, reflections, commitments and progress over time.',
      },
      {
        question: '29. Does TNT Coach AI track progress?',
        answer:
          'The app includes progress-oriented functionality, including saved coaching history and tools intended to help users review actions, insights, commitments and personal-development patterns.',
      },
      {
        question: '30. Does TNT Coach AI have a habit tracker?',
        answer:
          'Yes. The TNT Coach AI application includes Habit Tracker functionality to support consistency and behaviour-change goals.',
      },
      {
        question: '31. What is the Weekly Growth Report?',
        answer:
          'The Weekly Growth Report is intended to help users review patterns, commitments, wins and a suggested area of focus for the week ahead.',
      },
      {
        question: '32. Can I create or use coaching templates?',
        answer:
          "The TNT Coach AI application includes a My Templates area, allowing the platform to support repeatable coaching prompts or workflows associated with a user's own development needs.",
      },
    ],
  },
  {
    id: 'tnt-goals',
    title: 'Goals, Habits & Personal Growth',
    faqs: [
      {
        question: '33. Who can benefit from TNT Coach AI?',
        answer:
          'TNT Coach AI is designed for people who want structured self-coaching around personal goals, habits, confidence, discipline, business execution, leadership, motivation, purpose, decision-making or clearer thinking.',
      },
      {
        question: '34. When might someone use TNT Coach AI?',
        answer:
          'Example situations include feeling stuck, avoiding an important action, making a significant decision, preparing for a difficult conversation, setting a goal, working on a business, trying to stay disciplined, rebuilding motivation, improving confidence or resetting after overthinking.',
      },
      {
        question: '35. Can I use TNT Coach AI every day?',
        answer:
          "Yes. The platform is designed to support frequent self-coaching and includes a Daily Coaching Check-In, journaling and progress-oriented features. Access limits may depend on the user's chosen plan.",
      },
      {
        question: '36. Does TNT Coach AI tell users exactly what to do?',
        answer:
          "The app is intended to support reflection and action rather than replace a user's judgement. It can offer perspectives, questions and next-step suggestions, but users remain responsible for their own choices and should seek appropriate professional advice when needed.",
      },
    ],
  },
  {
    id: 'tnt-access',
    title: 'Access, Plans & Pricing',
    faqs: [
      {
        question: '37. Is there a free version of TNT Coach AI?',
        answer:
          'The website currently presents a Free plan at $0, including a limited number of coaching questions, access to selected coaching styles, a daily coaching check-in and basic journal history. Current pricing and feature availability should always be confirmed on the official pricing page before purchase.',
      },
      {
        question: '38. What paid plans are shown for TNT Coach AI?',
        answer:
          'The website currently presents Pro at $9.99 per month and Premium at $19.99 per month, with additional features at each level. Prices, plan names and inclusions can change, so users should check the live website or app for current details.',
      },
      {
        question: '39. Where can I access TNT Coach AI?',
        answer:
          'Information about the product is available at topnotchtechnologies.net. The web application is available at tnt-coach-ai.base44.app.',
      },
    ],
  },
  {
    id: 'tnt-safety',
    title: 'Scope, Safety & Important Information',
    faqs: [
      {
        question:
          '40. Is TNT Coach AI therapy, medical advice, financial advice or legal advice?',
        answer:
          'No. TNT Coach AI is presented as a personal-development and self-coaching tool. It is not therapy, medical advice, financial advice, legal advice or a substitute for qualified professional support. It also does not represent, imitate or claim endorsement from any real coach, athlete, author, public figure or organisation. Its coaching styles are original and based on broad personal-development principles.',
      },
    ],
  },
];

export const knowledgeEntity = [
  { label: 'Entity', value: 'TNT Coach AI' },
  { label: 'Brand', value: 'Top Notch Transformation' },
  { label: 'Business', value: 'Top Notch Technologies' },
  { label: 'Category', value: 'AI-powered personal development and self-coaching' },
];

export const knowledgeTopicsKeywords =
  'AI coaching, self-coaching, personal development, confidence, habits, goal setting, decision-making, procrastination, motivation, leadership, business growth, mindset, personal growth, journaling and progress tracking.';